import React, { useState, useEffect, useRef } from "react";

export interface ImageReorderModalProps {
  /** Whether the modal is visible */
  open: boolean;
  /** Files to preview and reorder */
  files: File[];
  /** Called with the final ordered files when user clicks Upload */
  onConfirm: (orderedFiles: File[]) => void;
  /** Called when user cancels */
  onCancel: () => void;
}

/**
 * Modal that lets the user reorder files (via drag-and-drop OR touch) on
 * thumbnails before they are actually uploaded.
 * Triggered only on drag & drop path.
 */
export const ImageReorderModal: React.FC<ImageReorderModalProps> = ({
  open,
  files,
  onConfirm,
  onCancel,
}) => {
  const [items, setItems] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const dragIndexRef = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const touchIndexRef = useRef<number | null>(null);
  const cloneRef = useRef<HTMLElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setItems(files);
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [open, files]);

  if (!open) return null;

  const handleDragStart = (index: number) => {
    dragIndexRef.current = index;
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    setDragOverIndex(null);
    const fromIndex = dragIndexRef.current;
    if (fromIndex === null || fromIndex === toIndex) return;
    reorder(fromIndex, toIndex);
    dragIndexRef.current = null;
  };

  const handleDragEnd = () => {
    dragIndexRef.current = null;
    setDragOverIndex(null);
  };

  /** Returns the thumb index whose DOM node contains the given point, or null */
  const indexFromPoint = (x: number, y: number): number | null => {
    // Temporarily hide the floating clone so elementFromPoint sees the grid
    if (cloneRef.current) cloneRef.current.style.display = "none";
    const el = document.elementFromPoint(x, y) as HTMLElement | null;
    if (cloneRef.current) cloneRef.current.style.display = "";

    if (!el || !gridRef.current) return null;
    const thumb = el.closest<HTMLElement>("[data-thumb-index]");
    if (!thumb) return null;
    return parseInt(thumb.dataset.thumbIndex ?? "", 10);
  };

  const handleTouchStart = (
    e: React.TouchEvent<HTMLDivElement>,
    index: number,
  ) => {
    touchIndexRef.current = index;
    const touch = e.touches[0];
    const source = e.currentTarget;
    const rect = source.getBoundingClientRect();

    // Build a visual clone that follows the finger
    const clone = source.cloneNode(true) as HTMLElement;
    clone.style.cssText = `
      position:fixed;
      top:${rect.top}px;
      left:${rect.left}px;
      width:${rect.width}px;
      height:${rect.height}px;
      opacity:0.85;
      pointer-events:none;
      z-index:10000;
      border-radius:10px;
      box-shadow:0 8px 24px rgba(0,0,0,0.3);
      transform:scale(1.06);
      transition:none;
    `;
    document.body.appendChild(clone);
    cloneRef.current = clone;

    // Offset within the element where the touch landed
    (clone as any)._offsetX = touch.clientX - rect.left;
    (clone as any)._offsetY = touch.clientY - rect.top;
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (touchIndexRef.current === null) return;
    e.preventDefault();

    const touch = e.touches[0];
    if (cloneRef.current) {
      const ox = (cloneRef.current as any)._offsetX ?? 0;
      const oy = (cloneRef.current as any)._offsetY ?? 0;
      cloneRef.current.style.left = `${touch.clientX - ox}px`;
      cloneRef.current.style.top = `${touch.clientY - oy}px`;
    }

    const over = indexFromPoint(touch.clientX, touch.clientY);
    setDragOverIndex(over);
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    const touch = e.changedTouches[0];
    const toIndex = indexFromPoint(touch.clientX, touch.clientY);
    const fromIndex = touchIndexRef.current;

    if (cloneRef.current) {
      cloneRef.current.remove();
      cloneRef.current = null;
    }
    touchIndexRef.current = null;
    setDragOverIndex(null);

    if (fromIndex !== null && toIndex !== null && fromIndex !== toIndex) {
      reorder(fromIndex, toIndex);
    }
  };

  const reorder = (from: number, to: number) => {
    setItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setPreviews((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h3 style={styles.title}>Reorder Images</h3>
        <p style={styles.subtitle}>
          Drag (or touch-drag) thumbnails into the order you want, then click{" "}
          <strong>Upload</strong>.
        </p>

        <div style={styles.grid} ref={gridRef}>
          {items.map((file, i) => (
            <div
              key={i}
              data-thumb-index={i}
              draggable
              onDragStart={() => handleDragStart(i)}
              onDragOver={(e) => handleDragOver(e, i)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, i)}
              onDragEnd={handleDragEnd}
              onTouchStart={(e) => handleTouchStart(e, i)}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={{
                ...styles.thumbWrapper,
                ...(dragOverIndex === i ? styles.thumbWrapperOver : {}),
              }}
              title={file.name}
            >
              <div style={styles.badge}>{i + 1}</div>
              <img
                src={previews[i]}
                alt={file.name}
                style={styles.thumb}
                draggable={false}
              />
              <div style={styles.thumbLabel} title={file.name}>
                {file.name}
              </div>
            </div>
          ))}
        </div>

        <div style={styles.footer}>
          <button style={styles.cancelBtn} onClick={onCancel}>
            Cancel
          </button>
          <button style={styles.uploadBtn} onClick={() => onConfirm(items)}>
            Upload ({items.length})
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },
  modal: {
    background: "#fff",
    borderRadius: 14,
    padding: "28px 28px 20px",
    maxWidth: 740,
    width: "90vw",
    maxHeight: "85vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 12px 40px rgba(0,0,0,0.22)",
    overflow: "hidden",
  },
  title: {
    margin: "0 0 4px 0",
    fontSize: 18,
    fontWeight: 700,
    color: "#222",
  },
  subtitle: {
    margin: "0 0 18px 0",
    fontSize: 13,
    color: "#777",
  },
  grid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    overflowY: "auto",
    flex: 1,
    paddingBottom: 4,
  },
  thumbWrapper: {
    position: "relative",
    width: 130,
    cursor: "grab",
    border: "2px solid #e5e5e5",
    borderRadius: 10,
    overflow: "hidden",
    background: "#f9f9f9",
    userSelect: "none",
    transition: "border-color 0.15s, transform 0.15s",
  },
  thumbWrapperOver: {
    borderColor: "#ff8038",
    transform: "scale(1.04)",
  },
  badge: {
    position: "absolute",
    top: 6,
    left: 6,
    background: "#ff8038",
    color: "#fff",
    width: 22,
    height: 22,
    borderRadius: "50%",
    fontSize: 12,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
    boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
  },
  thumb: {
    width: "100%",
    height: 120,
    objectFit: "cover",
    display: "block",
  },
  thumbLabel: {
    padding: "5px 6px",
    fontSize: 11,
    color: "#555",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 20,
  },
  cancelBtn: {
    padding: "8px 20px",
    border: "2px solid #ccc",
    background: "#fff",
    color: "#555",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
  },
  uploadBtn: {
    padding: "8px 24px",
    border: "none",
    background: "#ff8038",
    color: "#fff",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
  },
};

export default ImageReorderModal;
